import { useEffect, useState, useRef } from "react";
import api, { WINDOWS, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Check, AlertTriangle, MapPin, Footprints, Camera, Play, Timer } from "lucide-react";

function fmtDur(secs) {
  if (!secs || secs < 0) return "";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
}

export default function WorkerView() {
  const [route, setRoute] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [workerFilter, setWorkerFilter] = useState("all"); // 'all' | worker_id | 'unassigned'
  const [winFilter, setWinFilter] = useState("all");       // 'all' or window key
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const tickRef = useRef(null);

  // 1s timer for live "started Xm ago" countdown on in-progress stops
  useEffect(() => {
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (winFilter !== "all") params.set("window", winFilter);
    if (workerFilter === "unassigned") {
      // server side filter not supported — fetch all and filter client-side
    } else if (workerFilter !== "all") {
      params.set("worker_id", workerFilter);
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    const { data } = await api.get(`/worker/route${qs}`);
    let filtered = data;
    if (workerFilter === "unassigned") filtered = data.filter((d) => !d.assigned_worker_id);
    setRoute(filtered);
    setLoading(false);
  };

  useEffect(() => { api.get("/admin/workers").then((r) => setWorkers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workerFilter, winFilter]);

  const startPickup = async (id) => {
    try {
      await api.put(`/worker/orders/${id}`, { action: "start" });
      toast.success("Timer started ⏱");
      load();
    } catch {
      toast.error("Could not start");
    }
  };

  const pickedUp = async (id) => {
    if (!window.confirm("Mark this pickup as DONE?")) return;
    await api.put(`/worker/orders/${id}`, { action: "picked_up" });
    toast.success("Nice work! ✅");
    load();
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("bd_token");
      const res = await fetch(`${BACKEND_URL}/api/worker/orders/${id}/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Photo uploaded 📸");
      load();
    } catch (e) { toast.error("Upload failed"); }
  };

  const reportIssue = async (id) => {
    const note = window.prompt("What happened? (e.g. no bags out, hazardous item)");
    if (!note) return;
    await api.put(`/worker/orders/${id}`, { action: "issue", note });
    toast.success("Reported. Mom/Dad will check.");
    load();
  };

  const setAction = async (id, action) => {
    const labels = {
      no_bags_out: "Mark NO BAGS OUT?",
      access_blocked: "Mark ACCESS BLOCKED?",
      restricted_item: "Mark RESTRICTED ITEM (left in place)?",
      weather_delay: "Mark WEATHER DELAY?",
      admin_review: "Send to MOM/DAD for review?",
    };
    if (!window.confirm(labels[action] || "Confirm?")) return;
    await api.put(`/worker/orders/${id}`, { action });
    toast.success("Reported to admin");
    load();
  };

  // Today's stats
  const totalAssigned = route.length;
  const totalDone = route.filter((r) => r.status === "picked_up").length;
  const inProgress = route.filter((r) => r.status === "scheduled" && r.pickup_started_at).length;

  return (
    <div className="max-w-md mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-amber-400 border-b-4 border-amber-700 flex items-center justify-center">
          <Footprints className="text-white" strokeWidth={2.5} size={24}/>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-fredoka text-2xl font-bold" data-testid="worker-title">Today&apos;s Dash Route</h1>
          <p className="text-xs text-slate-500">Tap <b>Start</b> when you walk up. <b>Picked Up</b> stops the clock.</p>
        </div>
      </div>

      {/* Today progress strip */}
      <div className="card-clay p-3 mb-3 bg-slate-900 text-white" data-testid="worker-progress-strip">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">My progress</div>
            <div className="font-fredoka text-xl font-bold">{totalDone}/{totalAssigned} done</div>
          </div>
          {inProgress > 0 && (
            <div className="rounded-xl bg-amber-400/20 border border-amber-300 px-3 py-1.5 text-xs text-amber-200 font-bold inline-flex items-center gap-1">
              <Timer size={14}/> {inProgress} in progress
            </div>
          )}
        </div>
        <div className="h-2 mt-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-green-400 transition-all" style={{ width: `${totalAssigned ? Math.round((totalDone / totalAssigned) * 100) : 0}%` }} />
        </div>
      </div>

      {/* Worker filter — only show when there is more than one worker */}
      {workers.length > 1 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">Show stops for</div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setWorkerFilter("all")} className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 ${workerFilter === "all" ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700"}`} data-testid="worker-filter-all">Everyone</button>
            {workers.filter((w) => w.active !== false).map((w) => (
              <button key={w.id} onClick={() => setWorkerFilter(w.id)} className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 ${workerFilter === w.id ? "text-white" : "bg-white text-slate-700"}`} style={workerFilter === w.id ? { backgroundColor: w.color, borderColor: w.color } : { borderColor: w.color }} data-testid={`worker-filter-${w.key}`}>
                {w.display_name}
              </button>
            ))}
            <button onClick={() => setWorkerFilter("unassigned")} className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 ${workerFilter === "unassigned" ? "bg-amber-400 border-amber-600 text-slate-900" : "bg-white border-amber-300 text-amber-800"}`} data-testid="worker-filter-unassigned">Unassigned</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-5 gap-1.5 mb-4">
        <button onClick={() => setWinFilter("all")} className={`rounded-xl py-2 text-xs font-bold ${winFilter === "all" ? "bg-slate-900 text-white border-b-4 border-slate-700" : "bg-white border-2 border-slate-200 text-slate-700"}`} data-testid="worker-window-all">All</button>
        {WINDOWS.map((win) => (
          <button key={win.value} onClick={() => setWinFilter(win.value)} className={`rounded-xl py-2 text-xs font-bold ${winFilter === win.value ? "bg-green-500 text-white border-b-4 border-green-700" : "bg-white border-2 border-slate-200 text-slate-700"}`} data-testid={`worker-window-${win.value}`}>
            {win.short}
          </button>
        ))}
      </div>

      {loading ? <div className="text-slate-500 text-center py-10">Loading…</div> : (
        route.length === 0 ? (
          <div className="card-clay p-8 text-center" data-testid="worker-empty">
            <div className="text-5xl mb-2">🎉</div>
            <div className="font-fredoka font-bold text-lg">All done!</div>
            <div className="text-sm text-slate-500 mt-1">No pickups left for this filter.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="worker-route">
            {route.map((r, i) => {
              const aw = workers.find((w) => w.id === r.assigned_worker_id);
              const done = r.status === "picked_up";
              const started = !!r.pickup_started_at && !done;
              const liveDur = started
                ? Math.max(0, Math.round((Date.now() - new Date(r.pickup_started_at).getTime()) / 1000))
                : null;
              return (
                <div key={r.id + tick * 0} className={`card-clay p-5 ${done ? "border-green-300 bg-green-50" : started ? "border-amber-300 bg-amber-50" : "border-green-100"}`} data-testid={`worker-stop-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-1.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Stop {i + 1}</span>
                    <div className="flex items-center gap-1.5">
                      {aw && (
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: aw.color }} data-testid={`worker-stop-assigned-${i}`}>
                          {aw.display_name}
                        </span>
                      )}
                      <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{r.window?.replace("_", " ")}</span>
                    </div>
                  </div>
                  <div className="font-fredoka text-2xl font-bold text-slate-900 mt-2">{r.community}</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <Tile label="Building" value={r.building}/>
                    <Tile label="Unit" value={r.unit}/>
                    <Tile label="Bags" value={r.bags}/>
                    <Tile label="Window" value={(r.window || "").replace("_", " ").toUpperCase()}/>
                  </div>
                  {r.location_instructions && (
                    <div className="mt-3 rounded-xl bg-amber-50 border-2 border-amber-100 p-3 text-sm">
                      <div className="flex items-center gap-2 text-amber-800 font-bold text-xs uppercase tracking-wider"><MapPin size={14}/> Where</div>
                      <div className="text-slate-700 mt-1">{r.location_instructions}</div>
                    </div>
                  )}

                  {(started || done) && (
                    <div className="mt-3 rounded-xl bg-slate-900 text-white p-3 flex items-center justify-between" data-testid={`worker-timer-${i}`}>
                      <div className="flex items-center gap-2">
                        <Timer size={18} className="text-amber-300"/>
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">{done ? "Total time" : "Live"}</div>
                          <div className="font-fredoka text-xl font-bold">{done ? fmtDur(r.pickup_duration_seconds) : fmtDur(liveDur)}</div>
                        </div>
                      </div>
                      {done && <span className="text-green-400 font-bold uppercase text-xs">✓ Done</span>}
                    </div>
                  )}

                  {!done && (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {!started ? (
                        <button onClick={() => startPickup(r.id)} className="btn-push-amber py-4 inline-flex items-center justify-center gap-2 text-base" data-testid={`worker-start-${i}`}>
                          <Play size={20} strokeWidth={3}/> Start
                        </button>
                      ) : (
                        <button onClick={() => startPickup(r.id)} className="btn-push-outline py-4 inline-flex items-center justify-center gap-2 text-sm" data-testid={`worker-restart-${i}`}>
                          <Play size={16} strokeWidth={3}/> Restart timer
                        </button>
                      )}
                      <button onClick={() => pickedUp(r.id)} className="btn-push-green py-4 inline-flex items-center justify-center gap-2 text-base" data-testid={`worker-pick-${i}`}>
                        <Check size={20} strokeWidth={3}/> Picked Up
                      </button>
                    </div>
                  )}

                  {!done && (
                    <>
                      <label className="mt-3 btn-push-outline py-3 inline-flex items-center justify-center gap-2 cursor-pointer text-sm w-full" data-testid={`worker-photo-${i}`}>
                        <Camera size={16} strokeWidth={2.5}/> Add proof photo (optional)
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadPhoto(r.id, e.target.files?.[0])}/>
                      </label>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button onClick={() => setAction(r.id, "no_bags_out")} className="rounded-xl py-2.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 border-2 border-slate-200 text-slate-700" data-testid={`worker-nobags-${i}`}>🛑 No Bags Out</button>
                        <button onClick={() => setAction(r.id, "access_blocked")} className="rounded-xl py-2.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 border-2 border-slate-200 text-slate-700" data-testid={`worker-blocked-${i}`}>🚪 Access Blocked</button>
                        <button onClick={() => setAction(r.id, "restricted_item")} className="rounded-xl py-2.5 text-xs font-bold bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-800" data-testid={`worker-restricted-${i}`}>⚠️ Restricted Item</button>
                        <button onClick={() => setAction(r.id, "weather_delay")} className="rounded-xl py-2.5 text-xs font-bold bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 text-blue-800" data-testid={`worker-weather-${i}`}>⛈️ Weather Delay</button>
                      </div>
                      <button onClick={() => reportIssue(r.id)} className="mt-2 w-full rounded-xl py-2.5 text-xs font-bold bg-amber-50 hover:bg-amber-100 border-2 border-amber-200 text-amber-800 flex items-center justify-center gap-1.5" data-testid={`worker-issue-${i}`}>
                        <AlertTriangle size={14} strokeWidth={3}/> Report Issue
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

const Tile = ({ label, value }) => (
  <div className="rounded-xl bg-slate-50 p-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-fredoka font-bold text-xl text-slate-900">{value}</div>
  </div>
);
